import { ui } from '@beelday/common'
import { GrowthColumnContainer } from 'common/presentation/containers'
import { RootState } from 'common/redux'
import { TrainingRoomPhase } from 'common/types'
import { useQuestions, VotingResultsModal } from 'features/questions'
import { ScriptNavigation } from 'features/script'
import { FC } from 'react'
import { use100vh } from 'react-div-100vh'
import { useSelector } from 'react-redux'
import { ErrorWithStreams } from 'room/common/error-with-streams'
import { RoomContainer } from 'room/common/presentation/room-container'
import RoomHeader from 'room/common/presentation/room-header'
import { VideoLayoutResolver } from 'room/common/presentation/video-layout/video-layout-resolver'
import { roomEndUsers } from 'room/common/redux'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { MiscPanel } from 'room/misc'
import ConfiguredReviewTask from 'room/training-room/review-tasks/configured-review-trainer'
import { SelectGroupPanelTrainerRedux } from 'room/training-room/set-groups/select-group-panel-trainer'
import TrainerMenu from 'room/training-room/trainer-menu'
import { PresenterStyle } from 'video-conference-media/presentation/presenter-grid-layout'
import { SelfStreamView } from 'video-conference-media/presentation/top-right-stream-view'
import { VideoGrid } from 'video-conference-media/presentation/video-grid'
import { othersStream } from 'video-conference-media/redux'
import { selectFocusedFeatureType } from './redux'
import { TrainerFocusedFeature } from './trainer-focused-feature'

export const RoomTrainer: FC = () => {
	const containerHeight = use100vh()

	return (
		<RoomContainer
			style={{
				height: `${containerHeight}px`,
			}}
		>
			<RoomHeader streamView={<SelfStreamView />}>
				<ScriptNavigation />
			</RoomHeader>
			<MainContents presenterStyle="distinguished" />

			<MiscPanel />
			<ui.BeeldayLogoAbsoluteBottom />
		</RoomContainer>
	)
}

const MainContents: FC<{ presenterStyle: PresenterStyle }> = ({
	presenterStyle,
}) => {
	const questions = useQuestions()
	const roomAddress = useAssertedJoinedRoomAddress()
	const trainingRoomPhase = useSelector(
		(state: RootState) => state.room.trainingRoomPhase
	)
	const focusedFeature = useSelector(selectFocusedFeatureType)
	const sceneVideoUsers = useSelector(othersStream)
	// "How many people could answer", not "how many streams are on screen".
	// A camera-off participant has no stream but still votes, and an in-person
	// room has no streams at all - counting streams turns "3 of 12" into
	// "3 of 0". The participant side of this modal already counts people
	// (`features/questions/voting-modal.tsx`).
	const endUsers = useSelector(roomEndUsers)
	const showingFocusedFeature =
		focusedFeature && trainingRoomPhase === TrainingRoomPhase.INITIAL

	switch (trainingRoomPhase) {
		case TrainingRoomPhase.INITIAL:
			return (
				<>
					{showingFocusedFeature ? (
						<VideoLayoutResolver
							focusedFeature={focusedFeature}
							presenterStyle={presenterStyle}
							streams={sceneVideoUsers}
						>
							<TrainerFocusedFeature streams={sceneVideoUsers} />
						</VideoLayoutResolver>
					) : (
						<>
							{questions.active ? (
								<VotingResultsModal
									roomAddress={roomAddress}
									totalNumberOfUsers={endUsers.length}
								/>
							) : null}
							<VideoGrid
								sceneVideoUsers={sceneVideoUsers}
								presenterStyle={presenterStyle}
							/>
						</>
					)}
					<TrainerMenu />
				</>
			)
		case TrainingRoomPhase.REVIEWING_TASKS:
			return <ConfiguredReviewTask />
		case TrainingRoomPhase.SELECTING_GROUPS:
			return (
				<>
					<GrowthColumnContainer>
						<SelectGroupPanelTrainerRedux />
					</GrowthColumnContainer>
					<TrainerMenu />
				</>
			)
		default:
			return <ErrorWithStreams />
	}
}
